import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal, SidebarNoticeBox, Text,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useCreatePublicationOrderModal from './hooks/useCreatePublicationOrderModal'

const MODAL_KEY = 'CreatePublicationOrderModal'

type ModalCallbacks = {
  closeModal: () => void,
  createPublicationOrder: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreatePublicationOrderModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    publication: initPublication,
    publicationEdition: initPublicationEdition,
  } = modalPayload

  const hookPayload = useCreatePublicationOrderModal({
    publication: initPublication,
    publicationEdition: initPublicationEdition,
  })

  const {
    bookingDeadlinePassed,
    callbacks: {
      createPublicationOrder: createFn,
      getEmails,
    },
    creating,
    entityState: {
      ccEmail,
      email,
    },
    errors,
    handlers,
    publication,
    publicationEdition,
    saveEnabled,
    updating,
  } = hookPayload

  const createPublicationOrderPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: {
      publication_id: publication.id,
      publication_edition_id: publicationEdition.id,
      emails: getEmails(email, ccEmail),
    },
    requestOptions: {
      include_publication_order_bookings: true,
      include_publication_order_status: true,
    },
    toastText: 'Publication Order Created',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Create" titleSecondLine="Booking" />

      <SidebarModal.Content>
        {bookingDeadlinePassed && (
          <SidebarNoticeBox>
            <SidebarNoticeBox.Title>
              <FontAwesomeIcon color="red" icon={faExclamationTriangle} />
              <Text color="red" style={{ display: 'inline-block' }}>&nbsp;Alert</Text>
            </SidebarNoticeBox.Title>

            <SidebarNoticeBox.Paragraph>
              Booking Deadline has passed. Please confirm with your account manager before proceeding.
            </SidebarNoticeBox.Paragraph>
          </SidebarNoticeBox>
        )}

        {!bookingDeadlinePassed && (
          <SidebarNoticeBox>
            <SidebarNoticeBox.Title>
              <FontAwesomeIcon color="orange" icon={faExclamationTriangle} />
              <Text color="orange" style={{ display: 'inline-block' }}>&nbsp;Notice</Text>
            </SidebarNoticeBox.Title>

            <SidebarNoticeBox.Paragraph>
              Approving the booking will send your order to the publication and it will be charged to your account.
            </SidebarNoticeBox.Paragraph>
          </SidebarNoticeBox>
        )}

        <Form>
          <Form.Field label="* Publication" errorMessage={errors.email}>
            <input name="email" type="text" value={email} {...handlers} />
          </Form.Field>

          <Form.Field label="CC" marginTop="large">
            <input name="ccEmail" type="text" value={ccEmail} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => handleCallbackAction(createPublicationOrderPayload)}
          size="large"
        >
          {saveEnabled ? 'Confirm Booking' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreatePublicationOrderModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
